import apiClient, { extractApiPayloadMessage, getApiErrorMessage } from './apiClient';
import { SANDBOX_PREVIEW_TIMEOUT_MS } from '../utils/previewTiming';

const runSandboxPreviewAttempt = async ({ projectId }) => {
  const { data } = await apiClient.post(
    '/Sandbox/run',
    {
      ProjectId: projectId || null
    },
    { timeout: SANDBOX_PREVIEW_TIMEOUT_MS }
  );

  const apiErrorMessage = extractApiPayloadMessage(data);

  if (apiErrorMessage) {
    throw new Error(apiErrorMessage);
  }

  const result = data?.data || data;

  return {
    sessionId: result?.sessionId || result?.SessionId || null,
    executionId: result?.executionId || result?.ExecutionId || null,
    stdout: result?.stdout || result?.Stdout || '',
    stderr: result?.stderr || result?.Stderr || '',
    exitCode:
      typeof result?.exitCode === 'number'
        ? result.exitCode
        : typeof result?.ExitCode === 'number'
          ? result.ExitCode
          : null,
    durationMs:
      typeof result?.durationMs === 'number'
        ? result.durationMs
        : typeof result?.DurationMs === 'number'
          ? result.DurationMs
          : null,
    previewUrl: result?.previewUrl || result?.PreviewUrl || '',
    expiresAtUtc: result?.expiresAtUtc || result?.ExpiresAtUtc || null
  };
};

export const runSandboxPreviewApi = async ({ projectId }) => {
  try {
    return await runSandboxPreviewAttempt({ projectId });
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Sandbox preview failed.'));
  }
};

const normalizeDeployment = (deployment) => ({
  id: deployment?.id || deployment?.Id || '',
  projectId: deployment?.projectId || deployment?.ProjectId || '',
  domain: deployment?.domain || deployment?.Domain || '',
  status: deployment?.status || deployment?.Status || '',
  deployUrl: deployment?.deployUrl || deployment?.DeployUrl || '',
  buildExitCode:
    typeof deployment?.buildExitCode === 'number'
      ? deployment.buildExitCode
      : typeof deployment?.BuildExitCode === 'number'
        ? deployment.BuildExitCode
        : null,
  buildStdout: deployment?.buildStdout || deployment?.BuildStdout || '',
  buildStderr: deployment?.buildStderr || deployment?.BuildStderr || '',
  buildDurationMs:
    typeof deployment?.buildDurationMs === 'number'
      ? deployment.buildDurationMs
      : typeof deployment?.BuildDurationMs === 'number'
        ? deployment.BuildDurationMs
        : null,
  builtAtUtc: deployment?.builtAtUtc || deployment?.BuiltAtUtc || null,
  error: deployment?.error || deployment?.Error || null,
  createdAt: deployment?.createdAt || deployment?.CreatedAt || null,
  updatedAt: deployment?.updatedAt || deployment?.UpdatedAt || null
});

export const fetchDeploymentsApi = async ({ projectId } = {}) => {
  try {
    const { data } = await apiClient.get('/Sandbox/deployments', {
      params: projectId ? { projectId } : undefined
    });
    const payload = data?.data || data;
    const items = Array.isArray(payload) ? payload : [];

    return items.map(normalizeDeployment);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Deployments request failed.'));
  }
};

export const checkDomainAvailabilityApi = async ({ projectId, domain }) => {
  try {
    const { data } = await apiClient.get('/Sandbox/deployments/domain-availability', {
      params: {
        projectId,
        domain
      }
    });
    const payload = data?.data || data;

    return {
      domain: payload?.domain || payload?.Domain || domain,
      exists: Boolean(payload?.exists ?? payload?.Exists)
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Domain availability check failed.'));
  }
};

export const completeDeploymentApi = async ({ deploymentId }) => {
  try {
    const { data } = await apiClient.patch(`/Sandbox/deployments/${deploymentId}/complete`);
    const payload = data?.data || data;

    return normalizeDeployment(payload);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Complete deployment failed.'));
  }
};

export const createDeploymentApi = async ({ projectId, domain, provider = 'Http' }) => {
  try {
    const { data } = await apiClient.post(
      '/Sandbox/deploy',
      {
        ProjectId: projectId,
        Domain: domain,
        Provider: provider
      },
      {
        timeout: 130000
      }
    );
    const payload = data?.data || data;

    return normalizeDeployment(payload);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Create deployment failed.'));
  }
};
